import { useState } from "react";
import { CircularProgress, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { generateDownloadUrl } from "../../../services/taskerAttachments";

type Props = {
  attachmentId: string;
  fileName?: string;
  authMode?: "vendor" | "reviewer";
  emptyLabel?: string;
};

export function AttachmentPreviewLink({
  attachmentId,
  fileName,
  authMode = "vendor",
  emptyLabel = "(attached)",
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = fileName || emptyLabel;

  const openInNewTab = async () => {
    if (!attachmentId || loading) return;
    setLoading(true);
    setError(null);
    try {
      const url = await generateDownloadUrl(attachmentId, authMode);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Couldn't open file. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!attachmentId) {
    return (
      <Typography component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
        {label}
      </Typography>
    );
  }

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={openInNewTab}
        disabled={loading}
        underline="hover"
        sx={{
          fontSize: 13,
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: loading ? "wait" : "pointer",
          color: "primary.main",
        }}
        title={label}
      >
        {label}
        {loading ? (
          <CircularProgress size={12} sx={{ ml: 0.5 }} />
        ) : (
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        )}
      </Link>
      {error && (
        <Typography
          component="span"
          sx={{ fontSize: 11, color: "error.main", ml: 1 }}
        >
          {error}
        </Typography>
      )}
    </>
  );
}
